import { useState } from "react";
import axios from "axios";
import { X, Save, FileText } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

const parseDt = (d, t) => {
  if (!d) return null;
  const dt = new Date(t ? `${d}T${t}` : d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

export const BookingDetailModal = ({ booking, onClose, onSaved }) => {
  const { tours, fleet, fmtPrice, settings } = useSiteData();
  const [form, setForm] = useState({
    vehicle: booking.vehicle || "",
    duration_option: booking.duration_option || "",
    pickup_date: booking.pickup_date || "",
    pickup_time: booking.pickup_time || "",
    return_date: booking.return_date || "",
    return_time: booking.return_time || "",
    pickup_location: booking.pickup_location || "",
    dropoff_location: booking.dropoff_location || "",
    driver_name: booking.driver_name || "",
    driver_phone: booking.driver_phone || "",
    payment_status: booking.payment_status || "unpaid",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Live price preview — mirrors the backend ticket calculation.
  // Rule: vehicle selected -> days x price/day; otherwise tour -> guests x price.
  const tour = tours.find((t) => t.name === booking.tour);
  const vehicle = fleet.find((v) => v.name === form.vehicle);
  const item = vehicle
    ? { name: vehicle.name, unit: vehicle.price, unitLabel: "per day", kind: "vehicle", duration: vehicle.capacity }
    : tour
      ? { name: tour.name, unit: tour.sale_price || tour.price, unitLabel: "per guest", kind: "tour", duration: tour.duration }
      : null;
  const start = parseDt(form.pickup_date, form.pickup_time);
  const end = parseDt(form.return_date, form.return_time);
  let days = start && end && end > start ? Math.max(1, Math.ceil((end - start) / 86400000)) : null;
  if (!days && item?.kind === "tour" && item.duration) {
    const m = String(item.duration).match(/\d+/);
    days = m ? parseInt(m[0], 10) : null;
  }
  let pricing = null;
  if (form.duration_option === "half_day" || form.duration_option === "full_day") {
    const flat = Number(form.duration_option === "half_day" ? settings.half_day_price : settings.full_day_price) || 0;
    pricing = { qty: 1, flat: true, label: form.duration_option === "half_day" ? "Half Day" : "Full Day", unit: flat, total: flat };
  } else if (item && item.unit != null) {
    const qty = item.kind === "vehicle" ? days || 1 : Math.max(1, parseInt(booking.guests || "1", 10) || 1);
    pricing = { qty, label: item.kind === "vehicle" ? `${qty} day${qty > 1 ? "s" : ""}` : `${qty} guest${qty > 1 ? "s" : ""}`, unit: item.unit, total: qty * item.unit };
  }

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/enquiries/${booking.id}`, form, { withCredentials: true });
      toast.success("Booking details saved.");
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save the details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#0b132b]/50 backdrop-blur-sm" onClick={onClose} data-testid="booking-detail-overlay">
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-white p-6 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-detail-modal"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand)]">Booking {"VS-" + booking.id.slice(0, 8).toUpperCase()}</p>
            <h3 className="mt-1 font-display font-bold text-xl text-[#0b132b]">{booking.name} — {booking.tour || booking.route || "Enquiry"}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{booking.email}{booking.phone ? ` · ${booking.phone}` : ""}</p>
          </div>
          <button onClick={onClose} data-testid="booking-detail-close" aria-label="Close" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Vehicle (from fleet)</span>
            <select data-testid="detail-vehicle-select" value={form.vehicle} onChange={set("vehicle")} className={`${inputCls} mt-1`}>
              <option value="">No vehicle</option>
              {fleet.map((v) => (
                <option key={v.id} value={v.name}>{`${v.name} — ${fmtPrice(v.price)}/day`}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Pickup date</span>
            <input data-testid="detail-pickup-date" type="date" value={form.pickup_date} onChange={set("pickup_date")} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Pickup time</span>
            <input data-testid="detail-pickup-time" type="time" value={form.pickup_time} onChange={set("pickup_time")} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Return date</span>
            <input data-testid="detail-return-date" type="date" value={form.return_date} onChange={set("return_date")} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Return time</span>
            <input data-testid="detail-return-time" type="time" value={form.return_time} onChange={set("return_time")} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Pickup location</span>
            <input data-testid="detail-pickup-location" value={form.pickup_location} onChange={set("pickup_location")} placeholder="e.g. Zurich Airport" className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Drop-off location</span>
            <input data-testid="detail-dropoff-location" value={form.dropoff_location} onChange={set("dropoff_location")} placeholder="e.g. Hotel lobby" className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Driver name (optional)</span>
            <input data-testid="detail-driver-name" value={form.driver_name} onChange={set("driver_name")} placeholder="Leave empty if no driver" className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Driver phone</span>
            <input data-testid="detail-driver-phone" value={form.driver_phone} onChange={set("driver_phone")} className={`${inputCls} mt-1`} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Tour duration package</span>
            <select data-testid="detail-duration-select" value={form.duration_option} onChange={set("duration_option")} className={`${inputCls} mt-1`}>
              <option value="">No flat package — price from tour/vehicle</option>
              <option value="half_day">{`Half Day — ${fmtPrice(Number(settings.half_day_price) || 0)}`}</option>
              <option value="full_day">{`Full Day — ${fmtPrice(Number(settings.full_day_price) || 0)}`}</option>
            </select>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Payment status</span>
            <select data-testid="detail-payment-status" value={form.payment_status} onChange={set("payment_status")} className={`${inputCls} mt-1`}>
              <option value="unpaid">Unpaid</option>
              <option value="deposit">Deposit received</option>
              <option value="paid">Paid</option>
            </select>
          </label>
        </div>

        <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="detail-price-preview">
          {days != null && (
            <div>
              <p className="text-2xl font-display font-extrabold text-[var(--brand)]">{days}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">day{days > 1 ? "s" : ""}</p>
            </div>
          )}
          {pricing ? (
            <>
              <div>
                <p className="text-sm font-bold text-[#0b132b]">{pricing.flat ? `${pricing.label} — flat rate` : `${pricing.label} × ${fmtPrice(pricing.unit)}`}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{pricing.flat ? "duration package · auto-calculated" : `${item.unitLabel} · auto-calculated`}</p>
              </div>
              <div>
                <p className="text-2xl font-display font-extrabold text-[#0b132b]" data-testid="detail-price-total">{fmtPrice(pricing.total)}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">total</p>
              </div>
            </>
          ) : (
            <p className="text-sm font-semibold text-slate-500">Price on request — no matching tour or vehicle price found.</p>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-2.5">
          <button
            data-testid="detail-save-button"
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-xs font-bold px-5 py-2.5 transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save details"}
          </button>
          <button
            data-testid="detail-open-ticket-button"
            onClick={() => window.open(`/admin/ticket/${booking.id}`, "_blank")}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0b132b] text-white text-xs font-bold px-5 py-2.5 transition-colors hover:bg-slate-700"
          >
            <FileText className="w-3.5 h-3.5" /> Preview &amp; send ticket
          </button>
        </div>
      </div>
    </div>
  );
};
